import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { GlobalLoadingService, LoadingContext } from '../services/global-loading.service';

/**
 * Configuration for loading interceptor
 *
 * Allows fine-grained control over which requests trigger loading state
 */
export interface LoadingInterceptorConfig {
  /** URL patterns to exclude from loading indicator (exact match or partial) */
  excludeUrls?: string[];

  /** HTTP methods to exclude (e.g., 'GET', 'HEAD', 'OPTIONS') */
  excludeMethods?: string[];

  /** Default loading message when no context message provided */
  defaultMessage?: string;

  /** Optional predicate function to skip loading for specific requests */
  skipLoading?: (req: HttpRequest<any>) => boolean;
}

/**
 * Default configuration for loading interceptor
 *
 * @private
 */
const DEFAULT_CONFIG: LoadingInterceptorConfig = {
  excludeUrls: ['/health', '/ping', '/metrics', '/telemetry'],
  excludeMethods: [],
  defaultMessage: 'Loading...',
};

/** Extracts a concise endpoint path from a full URL for debug display. */
function extractEndpointDisplay(url: string): string {
  try {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      const parsed = new URL(url);
      return parsed.pathname + (parsed.search || '');
    }
  } catch {
    // fallback to raw URL
  }
  return url;
}

/**
 * Loading HTTP Interceptor
 *
 * Automatically shows/hides global loader for all HTTP requests. Integrates with GlobalLoadingService to manage loading
 * state.
 *
 * Features:
 *
 * - Automatic show/hide for all HTTP requests
 * - Handles concurrent requests via request counting
 * - Cleans up on request completion (success or error)
 * - Configurable via URL patterns and HTTP methods to exclude specific endpoints
 * - Support for contextual loading metadata (feature, action, custom message)
 * - Default message fallback for requests without context
 *
 * @example
 *   ```typescript
 *   // In app.config.ts with default configuration
 *   provideHttpClient(
 *     withInterceptors([
 *       loadingInterceptor(),
 *       authInterceptor,
 *       errorInterceptor
 *     ])
 *   )
 *
 *   // With custom configuration
 *   provideHttpClient(
 *     withInterceptors([
 *       loadingInterceptor({
 *         excludeUrls: ['/health', '/ping', '/api/config'],
 *         excludeMethods: ['HEAD'],
 *         defaultMessage: 'Fetching data...',
 *         skipLoading: (req) => req.headers.has('X-Skip-Loading')
 *       }),
 *       authInterceptor,
 *       errorInterceptor
 *     ])
 *   )
 *   ```;
 */
export function loadingInterceptor(config: LoadingInterceptorConfig = DEFAULT_CONFIG): HttpInterceptorFn {
  return (req, next) => {
    const loadingService = inject(GlobalLoadingService);
    const finalConfig = { ...DEFAULT_CONFIG, ...config };

    // Determine if this request should show loading indicator
    const isExcludedByUrl = finalConfig.excludeUrls?.some((url) => req.url.includes(url));
    const isExcludedByMethod = finalConfig.excludeMethods?.some(
      (method) => req.method.toUpperCase() === method.toUpperCase()
    );
    const isSkippedByHeader = req.headers.get('X-Skip-Loading') === 'true';
    const isSkippedByPredicate = finalConfig.skipLoading?.(req);

    const shouldSkipLoader = isExcludedByUrl || isExcludedByMethod || isSkippedByHeader || isSkippedByPredicate;

    if (!shouldSkipLoader) {
      const customMessage = req.headers.get('X-Loading-Message');
      const endpoint = extractEndpointDisplay(req.url);
      const method = req.method.toUpperCase();

      // Show actual request endpoint reason by default; respect custom overrides if explicitly provided
      const message =
        customMessage ??
        (config.defaultMessage !== undefined && config.defaultMessage !== 'Loading...'
          ? config.defaultMessage
          : `[${method}] ${endpoint}`);

      // Extract context metadata from request
      const context: LoadingContext = {
        url: req.url,
        method: req.method,
        message,
      };

      // Show loader with context
      loadingService.showWithContext(context);
    }

    return next(req).pipe(
      finalize(() => {
        if (!shouldSkipLoader) {
          loadingService.hide();
        }
      })
    );
  };
}

/**
 * Pre-configured loading interceptor with default settings
 *
 * Use this if you want to use the interceptor without custom configuration
 *
 * @example
 *   ```typescript
 *   provideHttpClient(
 *     withInterceptors([loadingInterceptorDefault])
 *   )
 *   ```;
 */
export const loadingInterceptorDefault: HttpInterceptorFn = loadingInterceptor();
